// ONE REFRESH SIGNAL for the badge counts and every queue gallery (owner,
// 2026-09-25, docs/decisions.md D-160).
//
// The shell owns a single `RefreshSignal`. Each bump carries a sequence number
// and a flavour:
//
// - NOT SILENT — the header's Refresh button. The gallery may show its loading
//   spinner, and a failure shows the error banner, because somebody asked.
// - SILENT — the Pipeline button and the 30-second timer. No spinner, the rows
//   on screen stay until new rows arrive, and a failure is swallowed: a refresh
//   nobody asked for must not report its own failure.
//
// The counts and the galleries move on the same bump, so they cannot disagree.
//
// The decisions are pure functions here so the node-only test suite can pin
// them without rendering anything (`app/mock/tests/auto-refresh.test.ts`).

/** The background refresh period. The owner's ceiling is thirty seconds. */
export const AUTO_REFRESH_MS = 30_000

export interface RefreshSignal {
  /** Bumped once per refresh. A screen reacts to a change, never to the value. */
  readonly seq: number
  /** True for the Pipeline button and the timer; false for the Refresh button. */
  readonly silent: boolean
}

export const INITIAL_REFRESH: RefreshSignal = { seq: 0, silent: false }

/** The next signal after `prev`. */
export function nextRefresh(prev: RefreshSignal, silent: boolean): RefreshSignal {
  return { seq: prev.seq + 1, silent }
}

/** Whether a timer tick should refresh now. A hidden browser tab skips the tick
    and catches up with one silent refresh when it becomes visible again. */
export function shouldAutoRefresh(visibility: string | undefined): boolean {
  return visibility === undefined || visibility === 'visible'
}

type QueueRefreshAction = 'none' | 'reload' | 'reload-silent' | 'mark-stale'

/**
 * What a gallery does when the signal moves or its pause lifts.
 *
 * - `seqChanged` — the signal's `seq` differs from the last one this gallery saw.
 * - `silent` — the flavour of that signal.
 * - `paused` — the screen has its invoice modal or a dialog open, or a submit
 *   is in flight. The gallery must not move under it, so a signal only marks
 *   the gallery stale.
 * - `stale` — a signal arrived while paused and has not been applied yet. When
 *   the pause lifts, the gallery catches up with one silent reload.
 * - `extraPagesLoaded` — Archives only: the reader has pressed "Load more".
 *   A silent reload would throw those pages away, so only a Refresh-button
 *   reload (not silent) applies, and no catch-up runs.
 */
export function queueRefreshAction(a: {
  seqChanged: boolean
  silent: boolean
  paused: boolean
  stale: boolean
  extraPagesLoaded?: boolean
}): QueueRefreshAction {
  if (a.paused) return a.seqChanged ? 'mark-stale' : 'none'
  if (a.seqChanged) {
    if (!a.silent) return 'reload'
    return a.extraPagesLoaded ? 'none' : 'reload-silent'
  }
  if (a.stale && !a.extraPagesLoaded) return 'reload-silent'
  return 'none'
}

/**
 * Which expanded cards' line items to re-read after the gallery's rows change.
 *
 * `previous` and `current` map each row's id to its last-write stamp. A card is
 * re-read only when its own stamp moved, or when `nonceChanged` (the host asked
 * for every card). Entries for rows that left the gallery, or whose card is
 * collapsed, are dropped. A card that already shows line items keeps them on
 * screen while the re-read runs — it is never set back to a spinner.
 */
export function planLineItemRefresh(a: {
  previous: ReadonlyMap<string, string>
  current: ReadonlyMap<string, string>
  nonceChanged: boolean
  expanded: ReadonlySet<string>
  cached: Iterable<string>
}): { refetch: string[]; drop: string[] } {
  const refetch: string[] = []
  const drop: string[] = []
  for (const id of a.cached) {
    if (!a.current.has(id) || !a.expanded.has(id)) drop.push(id)
  }
  for (const [id, stamp] of a.current) {
    if (!a.expanded.has(id)) continue
    if (a.nonceChanged || a.previous.get(id) !== stamp) refetch.push(id)
  }
  return { refetch, drop }
}
